import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileSpreadsheet } from "lucide-react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import {
  fetchCatalogoPartidaDetalle,
  fetchCatalogoPartidas,
  fetchCatalogoTaxonomia,
  fetchCatalogoTipos,
} from "../lib/catalogoApi";
import { fmtEUR, parsePrecioCatalogo } from "../lib/storage";
import { cn } from "../lib/utils";
import { useGestorPartidasNav } from "../context/GestorPartidasNavContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { ScrollArea } from "../components/ui/scroll-area";

const TIPO_LABEL = {
  rehabilitacion: "Rehabilitación",
  obra_nueva: "Obra nueva",
  espacios_urbanos: "Espacios urbanos",
};

function mensajeErrorCatalogo(error, textoPorDefecto) {
  const d = error?.response?.data;
  if (d?.error === "base_datos_precios_no_configurada") {
    return "Catálogo SQL no configurado (variables DATABASE_URL / DB_* o mysqld fuera de marcha).";
  }
  if (d?.mensaje) return String(d.mensaje);
  if (typeof d?.error === "string") return d.error;
  if (error?.message) return String(error.message);
  return textoPorDefecto;
}

function idNum(x) {
  if (x == null || x === "") return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function buscarNodoEnArbol(raices, nodeId) {
  const t = idNum(nodeId);
  if (t == null) return null;
  function dfs(n) {
    if (idNum(n.id) === t) return n;
    if (!n.children?.length) return null;
    for (let i = 0; i < n.children.length; i += 1) {
      const x = dfs(n.children[i]);
      if (x) return x;
    }
    return null;
  }
  for (let i = 0; i < raices.length; i += 1) {
    const x = dfs(raices[i]);
    if (x) return x;
  }
  return null;
}

function idsRutaHastaNodo(raices, targetId) {
  const t = idNum(targetId);
  if (t == null) return new Set();
  const encontrados = [];
  function dfs(nodes, prefijo) {
    for (const n of nodes) {
      if (idNum(n.id) === t) {
        for (const x of prefijo) encontrados.push(idNum(x));
        encontrados.push(idNum(n.id));
        return true;
      }
      if (n.children?.length) {
        if (dfs(n.children, [...prefijo, n.id])) return true;
      }
    }
    return false;
  }
  dfs(raices, []);
  return new Set(encontrados.filter((x) => x != null));
}

function strEq(a, b) {
  return String(a ?? "") === String(b ?? "");
}

function codigoInferidoPartida(p) {
  if (!p || typeof p !== "object") return null;
  const v = p.codigo_inferido ?? p.codigoInferido;
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function tituloInferidoPartida(p) {
  if (!p || typeof p !== "object") return "";
  const v = p.titulo_inferido ?? p.tituloInferido;
  if (v == null) return "";
  return String(v);
}

function descripcionPartida(p) {
  if (!p || typeof p !== "object") return "";
  const v = p.descripcion_snippet ?? p.descripcionSnippet;
  if (v == null) return "";
  return String(v).trim();
}

function jsonDetalleParaInspeccion(obj) {
  try {
    return JSON.stringify(
      obj,
      (_k, v) => (typeof v === "bigint" ? v.toString() : v),
      2,
    );
  } catch {
    return String(obj);
  }
}

function filasTaxonomiaVisibles(raices, expandedId) {
  const abiertoRuta = idsRutaHastaNodo(raices, expandedId);
  const out = [];
  const stack = [...raices].reverse().map((nodo) => ({ nodo, depth: 0 }));
  while (stack.length) {
    const { nodo: n, depth } = stack.pop();
    const ch = n.children || [];
    const hasCh = ch.length > 0;
    out.push({ nodo: n, depth, hasCh });
    const nid = idNum(n.id);
    if (hasCh && nid != null && abiertoRuta.has(nid)) {
      for (let i = ch.length - 1; i >= 0; i -= 1) {
        stack.push({ nodo: ch[i], depth: depth + 1 });
      }
    }
  }
  return out;
}

function rutaNodosHasta(raices, targetId) {
  const t = idNum(targetId);
  if (t == null) return [];
  let acc = [];
  function dfs(nodes, stack) {
    for (const n of nodes) {
      if (idNum(n.id) === t) {
        acc = [...stack, n];
        return true;
      }
      if (n.children?.length && dfs(n.children, [...stack, n])) return true;
    }
    return false;
  }
  dfs(raices, []);
  return acc;
}

function filasArbolConPartidas(
  raices,
  expandedId,
  taxonomiaNodoIdSeleccionado,
  cargandoLista,
  partidas,
) {
  const base = filasTaxonomiaVisibles(raices, expandedId);
  const tid = idNum(taxonomiaNodoIdSeleccionado);
  const out = [];
  for (const row of base) {
    out.push({ kind: "tax", nodo: row.nodo, depth: row.depth, hasCh: row.hasCh });
    const nid = idNum(row.nodo.id);
    const hoja = !row.nodo.children || row.nodo.children.length === 0;
    if (tid != null && nid === tid && hoja) {
      if (cargandoLista) {
        out.push({ kind: "meta", depth: row.depth + 1, text: "Cargando partidas…" });
      } else if (!partidas.length) {
        out.push({
          kind: "meta",
          depth: row.depth + 1,
          text: "Sin partidas en esta categoría.",
        });
      } else {
        for (const p of partidas) {
          out.push({ kind: "partida", depth: row.depth + 1, partida: p });
        }
      }
    }
  }
  return out;
}

function formatearPathTitulos(pt) {
  if (pt == null) return null;
  if (typeof pt === "string") return pt.trim() ? pt : null;
  if (Array.isArray(pt)) {
    const s = pt.map((x) => String(x).trim()).filter(Boolean);
    return s.length ? s.join(" › ") : null;
  }
  if (typeof pt === "object") {
    const s = Object.entries(pt)
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
      .join(" · ");
    return s || null;
  }
  return String(pt);
}

function obtenerCeldasJustificacionFila(row) {
  const j = row.celdas_json ?? row.celdasJson;
  if (Array.isArray(j)) return j.map((x) => (x == null ? "" : String(x)));
  if (j && typeof j === "object")
    return Object.keys(j)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((k) => String(j[k] ?? ""));
  return [];
}

function clasificacionJustificacionFila(row) {
  return String(row.clasificacion_fila ?? row.clasificacionFila ?? "").trim();
}

function TablaJustificacionPrecio({ filas }) {
  if (!Array.isArray(filas) || filas.length === 0) return null;
  const ordenados = [...filas].sort(
    (a, b) => Number(a?.orden ?? 0) - Number(b?.orden ?? 0),
  );
  let maxCols = 1;
  for (const row of ordenados) {
    const cs = obtenerCeldasJustificacionFila(row);
    maxCols = Math.max(maxCols, cs.length);
  }
  function pad(cs) {
    const out = cs.slice();
    while (out.length < maxCols) out.push("");
    return out;
  }
  function esColumnaTipoPrecio(tipo, texto, ci, total) {
    if (tipo !== "linea_precio_descompuesto") return false;
    const raw = String(texto ?? "").trim();
    if (!raw) return false;
    if (ci < total - 2) return false;
    const num = parsePrecioCatalogo(raw);
    return num !== 0;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Justificación de precio
        </h3>
      </div>
      <div className="overflow-x-auto max-h-[min(70vh,720px)]">
        <table className="w-full text-sm border-collapse table-fixed min-w-[640px]">
          <tbody>
            {ordenados.map((row, ri) => {
              const tipo = clasificacionJustificacionFila(row);
              const cells = pad(obtenerCeldasJustificacionFila(row));
              const rk = row.id != null ? `jf-${String(row.id)}` : `jfr-${String(ri)}`;
              if (tipo === "grupo") {
                const txt = cells.filter((c) => String(c).trim()).join(" · ");
                return (
                  <tr key={rk} className="border-t border-slate-200 bg-slate-50">
                    <td
                      className="px-3 py-2 font-semibold text-slate-800 align-top whitespace-normal break-words"
                      colSpan={maxCols}
                    >
                      {txt || "—"}
                    </td>
                  </tr>
                );
              }
              if (tipo === "cabecera_precio") {
                return (
                  <tr key={rk} className="border-t border-slate-200 bg-slate-100">
                    {cells.map((text, ci) => (
                      <td
                        key={`${rk}-h-${String(ci)}`}
                        className="border-r border-slate-200 last:border-r-0 px-3 py-2 font-semibold text-slate-800 align-top whitespace-normal break-words"
                      >
                        {text}
                      </td>
                    ))}
                  </tr>
                );
              }
              const lineaPrecio = tipo === "linea_precio_descompuesto";
              return (
                <tr
                  key={rk}
                  className={cn(
                    "border-t border-slate-100",
                    lineaPrecio ? "bg-white hover:bg-slate-50/80" : "bg-white",
                  )}
                >
                  {cells.map((text, ci) => {
                    const precio = esColumnaTipoPrecio(tipo, text, ci, cells.length)
                      ? parsePrecioCatalogo(text)
                      : null;
                    const mostrarEUR = precio != null && precio !== 0;
                    const alignRight =
                      lineaPrecio &&
                      mostrarEUR;
                    return (
                      <td
                        key={`${rk}-d-${String(ci)}`}
                        className={cn(
                          "border-r border-slate-100 last:border-r-0 px-3 py-2 align-top text-slate-800 whitespace-normal break-words",
                          alignRight &&
                            "text-right tabular-nums font-medium text-slate-900",
                        )}
                      >
                        {mostrarEUR ? fmtEUR(precio) : text || "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TablaFilasCeldasPorOrden({ tituloEtiqueta, filas }) {
  if (!Array.isArray(filas) || filas.length === 0) return null;
  const ordenados = [...filas].sort(
    (a, b) => Number(a?.orden ?? 0) - Number(b?.orden ?? 0),
  );
  let maxCols = 1;
  for (const row of ordenados) {
    const cs = obtenerCeldasJustificacionFila(row);
    maxCols = Math.max(maxCols, cs.length);
  }
  function pad(cs) {
    const out = cs.slice();
    while (out.length < maxCols) out.push("");
    return out;
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {tituloEtiqueta ? (
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {tituloEtiqueta}
          </h3>
        </div>
      ) : null}
      <div className="overflow-x-auto max-h-[min(50vh,520px)]">
        <table className="w-full text-sm border-collapse table-fixed min-w-[520px]">
          <tbody>
            {ordenados.map((row, ri) => {
              const cells = pad(obtenerCeldasJustificacionFila(row));
              const rk = row.id != null ? `rf-${String(row.id)}` : `rfr-${String(ri)}`;
              return (
                <tr key={rk} className="border-t border-slate-100 bg-white hover:bg-slate-50/60">
                  {cells.map((text, ci) => (
                    <td
                      key={`${rk}-c-${String(ci)}`}
                      className="border-r border-slate-100 last:border-r-0 px-3 py-2 align-top text-slate-800 whitespace-normal break-words"
                    >
                      {String(text ?? "").trim() ? text : "—"}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PanelDetalle({ detalle, cargando, codigoConsulta }) {
  const [inspeccionAbierta, setInspeccionAbierta] = useState(false);

  if (cargando) {
    return (
      <div className="text-sm text-slate-500 p-6">Cargando detalle...</div>
    );
  }
  if (!detalle) {
    return (
      <div className="text-sm text-slate-500 p-6">
        Selecciona una partida en la taxonomía para ver el detalle.
      </div>
    );
  }

  const {
    partida,
    rutaTaxonomia,
    imagenesOpcion,
    justificacionFilas,
    pliegoCondicion,
    pliegoSecciones,
    residuosGenerado,
    residuosFilas,
    opcionesParametro,
  } = detalle;

  const codigoPartida = codigoInferidoPartida(partida);
  const codigoConsultaTrim =
    codigoConsulta != null && String(codigoConsulta).trim() !== ""
      ? String(codigoConsulta).trim()
      : null;
  const codigo = codigoPartida ?? codigoConsultaTrim ?? "—";
  const titulo = tituloInferidoPartida(partida);
  const precioFmt = fmtEUR(parsePrecioCatalogo(partida?.precio));
  const unidadMedidaPrecioRaw =
    partida?.unidad_medida_precio ?? partida?.unidadMedidaPrecio;
  const unidadMedidaPrecio =
    unidadMedidaPrecioRaw != null
      ? String(unidadMedidaPrecioRaw).trim()
      : "";
  const descStr = descripcionPartida(partida);
  const textoResiduosTrim = residuosGenerado?.texto_residuos
    ? String(residuosGenerado.texto_residuos).trim()
    : "";
  const tieneResiduosFilas =
    Array.isArray(residuosFilas) && residuosFilas.length > 0;
  const residuosInformado = Boolean(textoResiduosTrim) || tieneResiduosFilas;
  const hayImagenes = Array.isArray(imagenesOpcion) && imagenesOpcion.length > 0;
  const mostrarFilaImagenDoc = hayImagenes || Boolean(descStr);

  return (
    <div className="space-y-6 p-5 pb-10">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-200 pb-4">
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 shrink-0">
          Partida
        </span>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 flex-1 min-w-[200px]">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 leading-snug">
              {titulo || codigo}
            </h2>
            <p className="font-mono text-xs sm:text-sm text-slate-500 mt-0.5">{codigo}</p>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 shrink-0">
            <span className="text-xl sm:text-2xl font-semibold text-emerald-700 tabular-nums whitespace-nowrap">
              {precioFmt}
            </span>
            {unidadMedidaPrecio ? (
              <span className="text-sm sm:text-base font-medium text-slate-600 whitespace-nowrap">
                {unidadMedidaPrecio}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {mostrarFilaImagenDoc ? (
        <div
          className={cn(
            "grid gap-6 items-stretch",
            hayImagenes ? "lg:grid-cols-2 lg:gap-8" : "grid-cols-1 max-w-3xl",
          )}
        >
          {hayImagenes ? (
            <div className="min-w-0 relative">
              <Carousel className="w-full max-w-full">
                <CarouselContent className="-ml-2 sm:-ml-3">
                  {imagenesOpcion.map((im) => (
                    <CarouselItem key={im.id} className="pl-2 sm:pl-3 basis-full">
                      <a
                        href={im.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/10] outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <img
                          src={im.url}
                          alt=""
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                      </a>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious
                  variant="outline"
                  className="-left-0 left-1 sm:left-2 top-[42%] h-9 w-9 border-slate-200 bg-white/95 shadow-sm"
                />
                <CarouselNext
                  variant="outline"
                  className="-right-0 right-1 sm:right-2 top-[42%] h-9 w-9 border-slate-200 bg-white/95 shadow-sm"
                />
              </Carousel>
            </div>
          ) : null}
          <div className="min-w-0 flex flex-col">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Descripción
            </h3>
            <div className="mt-3 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap grow">
              {descStr || "Sin descripción."}
            </div>
          </div>
        </div>
      ) : null}

      {justificacionFilas?.length ? (
        <TablaJustificacionPrecio filas={justificacionFilas} />
      ) : null}

      <Accordion
        type="multiple"
        defaultValue={["pliego", "opciones"]}
        className="w-full space-y-2"
      >
        <AccordionItem
          value="pliego"
          className="rounded-xl border border-slate-200 bg-white/95 shadow-sm px-1"
        >
          <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
            Pliego y condiciones
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-5">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Condición general</div>
              {pliegoCondicion?.texto_pliego ? (
                <div className="text-sm whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 max-h-[360px] overflow-y-auto leading-relaxed">
                  {pliegoCondicion.texto_pliego}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Sin texto de pliego.</p>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Secciones</div>
              {pliegoSecciones?.length ? (
                <div className="space-y-3">
                  {pliegoSecciones.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm"
                    >
                      <div className="text-xs font-medium text-slate-500 mb-2">{s.clave_seccion}</div>
                      <div className="text-sm whitespace-pre-wrap leading-relaxed text-slate-800">
                        {s.contenido}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Sin secciones.</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="residuos"
          className="rounded-xl border border-slate-200 bg-white/95 shadow-sm px-1"
        >
          <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
            Residuos
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-4">
            {residuosInformado ? (
              <>
                {textoResiduosTrim ? (
                  <div className="text-sm whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 max-h-[240px] overflow-y-auto leading-relaxed text-slate-800">
                    {residuosGenerado.texto_residuos}
                  </div>
                ) : null}
                {tieneResiduosFilas ? (
                  <TablaFilasCeldasPorOrden
                    tituloEtiqueta="Residuos por fila"
                    filas={residuosFilas}
                  />
                ) : null}
              </>
            ) : (
              <p className="text-sm text-slate-500">Sin bloque de residuos.</p>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="opciones"
          className="rounded-xl border border-slate-200 bg-white/95 shadow-sm px-1"
        >
          <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
            Opciones elegidas
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            {opcionesParametro?.length ? (
              <div className="space-y-5">
                {opcionesParametro.map((g, gi) => {
                  const pathTxt = formatearPathTitulos(g.pathTitulos);
                  return (
                    <div
                      key={g.id}
                      className="rounded-2xl border border-slate-100 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-sm"
                    >
                      <div className="text-sm font-semibold text-slate-900">
                        Grupo {g.orden != null ? g.orden : gi + 1}
                      </div>
                      {pathTxt ? (
                        <p className="text-sm text-slate-600 mt-2 leading-snug">{pathTxt}</p>
                      ) : null}
                      <div className="mt-4 space-y-3">
                        {g.radios?.map((r) => (
                          <div
                            key={r.id ?? `${gi}-${r.input_id}-${r.orden}`}
                            className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 text-sm"
                          >
                            <span className="text-slate-900 font-medium">{r.etiqueta}</span>
                            {r.seleccionada ? (
                              <Badge className="text-[10px] shrink-0">Seleccionada</Badge>
                            ) : null}
                            {r.enlace_href ? (
                              <a
                                href={r.enlace_href}
                                className="text-blue-600 text-xs font-medium underline-offset-2 hover:underline ml-auto shrink-0"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Enlace
                              </a>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Sin opciones parametrizadas.</p>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Collapsible
        open={inspeccionAbierta}
        onOpenChange={setInspeccionAbierta}
        className="rounded-xl border border-slate-300/80 bg-slate-950/[0.02]"
      >
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-left text-sm font-semibold text-slate-600 hover:bg-slate-100/80 transition-colors">
          <span>Inspección técnica (datos completos)</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200",
              inspeccionAbierta && "rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="text-[11px] leading-relaxed font-mono p-4 max-h-[min(55vh,520px)] overflow-auto border-t border-slate-200 bg-slate-950 text-slate-100 rounded-b-xl">
            {jsonDetalleParaInspeccion(detalle)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default function GestorPartidas() {
  const { tipo: tipoParam } = useParams();
  const navigate = useNavigate();
  const { setGestorSidebarNav } = useGestorPartidasNav();
  const [tiposBackend, setTiposBackend] = useState([]);
  const [arbol, setArbol] = useState([]);
  const [taxonomiaNodoId, setTaxonomiaNodoId] = useState(null);
  const [partidas, setPartidas] = useState([]);
  const [cargandoArbol, setCargandoArbol] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [codigoSeleccionado, setCodigoSeleccionado] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [expandedTaxonomiaId, setExpandedTaxonomiaId] = useState(null);

  const tiposSelect = useMemo(
    () =>
      tiposBackend.length
        ? tiposBackend
        : ["obra_nueva", "rehabilitacion", "espacios_urbanos"],
    [tiposBackend],
  );
  const tipoPreferido =
    tiposSelect.includes("rehabilitacion") ? "rehabilitacion" : tiposSelect[0];
  const tipo = tiposSelect.includes(tipoParam) ? tipoParam : tipoPreferido;

  useEffect(() => {
    if (!tipoParam || !tiposSelect.length) return;
    if (!tiposSelect.includes(tipoParam)) {
      navigate(`/gestor-partidas/${tipoPreferido}`, { replace: true });
    }
  }, [tipoParam, tiposSelect, tipoPreferido, navigate]);

  const padrePorId = useMemo(() => {
    const m = new Map();
    function walk(nodes, parentId) {
      for (const n of nodes) {
        const nid = idNum(n.id);
        const pid = idNum(parentId);
        if (nid != null && pid != null) m.set(nid, pid);
        if (n.children?.length) walk(n.children, n.id);
      }
    }
    walk(arbol, null);
    return m;
  }, [arbol]);

  const rutaExpandidaIds = useMemo(
    () => idsRutaHastaNodo(arbol, expandedTaxonomiaId),
    [arbol, expandedTaxonomiaId],
  );

  const cargarDetalle = useCallback(
    async (codigoInferido) => {
      const codigo = codigoInferido == null ? "" : String(codigoInferido).trim();
      setCodigoSeleccionado(codigo || null);
      setDetalle(null);
      if (!tipo || !codigo) return;
      setCargandoDetalle(true);
      try {
        const d = await fetchCatalogoPartidaDetalle({
          tipo,
          codigoInferido: codigo,
        });
        setDetalle(d);
      } catch (e) {
        setDetalle(null);
        toast.error(mensajeErrorCatalogo(e, "No se pudo cargar el detalle de la partida."));
      } finally {
        setCargandoDetalle(false);
      }
    },
    [tipo],
  );

  const esPartidaActiva = useCallback(
    (p) => strEq(codigoSeleccionado, codigoInferidoPartida(p) ?? p.codigo_inferido),
    [codigoSeleccionado],
  );

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await fetchCatalogoTipos();
        const lista = d.tipos || [];
        if (!vivo) return;
        if (!lista.length) {
          setTiposBackend(["obra_nueva", "rehabilitacion", "espacios_urbanos"]);
          return;
        }
        setTiposBackend(lista);
      } catch {
        setTiposBackend(["obra_nueva", "rehabilitacion", "espacios_urbanos"]);
        toast.error("No se pudieron cargar los tipos; usando valores por defecto.");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (!tipo) return;
    let vivo = true;
    setCargandoArbol(true);
    setArbol([]);
    setTaxonomiaNodoId(null);
    setPartidas([]);
    setCodigoSeleccionado(null);
    setDetalle(null);
    setExpandedTaxonomiaId(null);
    (async () => {
      try {
        const d = await fetchCatalogoTaxonomia({ tipo });
        if (!vivo) return;
        const a = d.arbol || [];
        setArbol(a);
      } catch (e) {
        if (!vivo) return;
        setArbol([]);
        setExpandedTaxonomiaId(null);
        toast.error(mensajeErrorCatalogo(e, "Error al cargar la taxonomía."));
      } finally {
        if (vivo) setCargandoArbol(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [tipo]);

  useEffect(() => {
    if (!tipo || taxonomiaNodoId == null) return;
    let vivo = true;
    const nodo = buscarNodoEnArbol(arbol, taxonomiaNodoId);
    const esHoja =
      nodo != null && (!nodo.children || nodo.children.length === 0);

    if (!esHoja) {
      setCargandoLista(false);
      setPartidas([]);
      setCodigoSeleccionado(null);
      setDetalle(null);
      return () => {
        vivo = false;
      };
    }

    setCargandoLista(true);
    setCodigoSeleccionado(null);
    setDetalle(null);
    (async () => {
      try {
        const d = await fetchCatalogoPartidas({ tipo, taxonomiaNodoId });
        if (!vivo) return;
        setPartidas(d.partidas || []);
      } catch (e) {
        if (!vivo) return;
        setPartidas([]);
        toast.error(mensajeErrorCatalogo(e, "Error al cargar la lista de partidas."));
      } finally {
        if (vivo) setCargandoLista(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [arbol, tipo, taxonomiaNodoId]);

  const filasCompletas = useMemo(
    () =>
      filasArbolConPartidas(
        arbol,
        expandedTaxonomiaId,
        taxonomiaNodoId,
        cargandoLista,
        partidas,
      ),
    [arbol, expandedTaxonomiaId, taxonomiaNodoId, cargandoLista, partidas],
  );

  const rutaCrumb = useMemo(
    () => rutaNodosHasta(arbol, taxonomiaNodoId),
    [arbol, taxonomiaNodoId],
  );

  const alternarExpandidoTaxonomia = useCallback(
    (id) => {
      setExpandedTaxonomiaId((cur) => {
        const idN = idNum(id);
        const curN = idNum(cur);
        if (idN == null) return curN;
        if (curN != null && curN === idN) return padrePorId.get(idN) ?? null;
        return idN;
      });
    },
    [padrePorId],
  );

  useEffect(() => {
    setGestorSidebarNav({
      tiposSelect,
      tipo,
      cargandoArbol,
      arbol,
      rows: filasCompletas,
      taxonomiaNodoId,
      rutaExpandidaIds,
      alternarExpandidoTaxonomia,
      setTaxonomiaNodoId,
      setExpandedTaxonomiaId,
      cargarDetalle,
      esPartidaActiva,
    });
    return () => setGestorSidebarNav(null);
  }, [
    setGestorSidebarNav,
    tiposSelect,
    tipo,
    cargandoArbol,
    arbol,
    filasCompletas,
    taxonomiaNodoId,
    rutaExpandidaIds,
    alternarExpandidoTaxonomia,
    cargarDetalle,
    esPartidaActiva,
  ]);

  return (
    <div className="px-6 py-8 flex flex-col gap-4 box-border">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
          Base schema-database
        </div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">
          Gestor de partidas
        </h1>
      </div>

      <Breadcrumb>
        <BreadcrumbList className="flex-wrap text-slate-600 sm:gap-2">
          <BreadcrumbItem>
            {!taxonomiaNodoId && !codigoSeleccionado ? (
              <BreadcrumbPage>{TIPO_LABEL[tipo] || tipo}</BreadcrumbPage>
            ) : (
              <BreadcrumbLink asChild>
                <NavLink
                  to={`/gestor-partidas/${tipo}`}
                  className="hover:text-slate-900 transition-colors"
                  onClick={() => {
                    setTaxonomiaNodoId(null);
                    setExpandedTaxonomiaId(null);
                    setCodigoSeleccionado(null);
                    setDetalle(null);
                    setPartidas([]);
                  }}
                >
                  {TIPO_LABEL[tipo] || tipo}
                </NavLink>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {rutaCrumb.map((n, i) => {
            const nid = idNum(n.id);
            const ultimoSinPartida = !codigoSeleccionado && i === rutaCrumb.length - 1;
            return (
              <Fragment key={String(n.id)}>
                <BreadcrumbSeparator className="text-slate-300 [&>svg]:size-3.5" />
                <BreadcrumbItem className="max-w-[220px] sm:max-w-[320px] truncate">
                  {ultimoSinPartida ? (
                    <BreadcrumbPage className="truncate">
                      {n.titulo || n.codigo}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <button
                        type="button"
                        className="truncate text-left max-w-full hover:text-slate-900 transition-colors"
                        onClick={() => {
                          if (nid == null) return;
                          setTaxonomiaNodoId(nid);
                          setExpandedTaxonomiaId(nid);
                          setCodigoSeleccionado(null);
                          setDetalle(null);
                        }}
                      >
                        {n.titulo || n.codigo}
                      </button>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
          {codigoSeleccionado ? (
            <Fragment key="crumb-partida">
              <BreadcrumbSeparator className="text-slate-300 [&>svg]:size-3.5" />
              <BreadcrumbItem className="max-w-[280px] sm:max-w-[400px] truncate">
                <BreadcrumbPage className="truncate">
                  {cargandoDetalle && !detalle
                    ? codigoSeleccionado
                    : tituloInferidoPartida(detalle?.partida) || codigoSeleccionado}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </Fragment>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="min-w-0 flex flex-col border border-slate-200 rounded-lg bg-white overflow-hidden max-h-[min(92vh,calc(100vh-220px))]">
        <div className="px-4 py-2 border-b border-slate-200 flex flex-wrap items-center gap-3 w-full min-w-0">
          <span className="text-sm font-semibold text-slate-700 shrink-0">Detalle</span>
          <div className="flex flex-wrap items-center justify-end gap-1 min-h-[36px] shrink-0 ml-auto">
            {(detalle?.enlacesBc3Excel ?? [])
              .filter((e) => e?.data_href)
              .map((e, ei) => (
                <a
                  key={e.id != null ? String(e.id) : `bce-${ei}`}
                  href={String(e.data_href)}
                  target="_blank"
                  rel="noreferrer"
                  title={
                    `${e.name || e.title_link || ""}`.trim() || undefined
                  }
                  aria-label={
                    `${e.name || e.title_link || "Descargar Excel"}`.trim()
                  }
                  className={cn(
                    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-emerald-700",
                    "hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-300 transition-colors outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-emerald-500",
                  )}
                >
                  <FileSpreadsheet className="h-4 w-4" strokeWidth={2} />
                </a>
              ))}
          </div>
        </div>
        <ScrollArea className="h-[min(92vh,calc(100vh-260px))]">
          <PanelDetalle
            detalle={detalle}
            cargando={cargandoDetalle}
            codigoConsulta={codigoSeleccionado}
          />
        </ScrollArea>
      </div>
    </div>
  );
}
